import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { MessageCircle, Instagram, Phone, User, CalendarClock, MessageSquareText, StickyNote, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { getAppointments, updateAppointment } from "@/lib/dentiumApi";

const CH_ICON = { whatsapp: MessageCircle, instagram: Instagram };
const CH_COLOR = { whatsapp: "#25D366", instagram: "#E1306C" };
const CH_NAME = { whatsapp: "WhatsApp", instagram: "Instagram" };

const STATUS = {
  pendiente: { label: "Pendiente", cls: "bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200" },
  confirmada: { label: "Confirmada", cls: "bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-200" },
  cancelada: { label: "Cancelada", cls: "bg-red-100 text-red-700 border-red-300 hover:bg-red-200" },
};
const NEXT = { pendiente: "confirmada", confirmada: "cancelada", cancelada: "pendiente" };

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("es-ES", { weekday: "long", day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};
const fmtShort = (iso) => {
  try { return new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

export default function CitasPage() {
  const qc = useQueryClient();
  const [openId, setOpenId] = useState(null);
  const [notes, setNotes] = useState("");
  const { data: citas = [] } = useQuery({ queryKey: ["appointments"], queryFn: getAppointments, refetchInterval: 6000 });

  const selected = citas.find((c) => c.id === openId) || null;
  useEffect(() => { if (selected) setNotes(selected.internal_notes || ""); }, [openId]); // eslint-disable-line

  const update = useMutation({
    mutationFn: ({ id, payload }) => updateAppointment(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["appointments"] });
      qc.invalidateQueries({ queryKey: ["metrics"] });
    },
    onError: () => toast.error("No se pudo actualizar la cita"),
  });

  const cycleStatus = (cita, e) => {
    if (e) e.stopPropagation();
    const next = NEXT[cita.appointment_status || "pendiente"];
    update.mutate({ id: cita.id, payload: { appointment_status: next } },
      { onSuccess: () => toast.success(`Cita ${STATUS[next].label.toLowerCase()}`) });
  };

  const saveNotes = () => {
    if (!selected || notes === (selected.internal_notes || "")) return;
    update.mutate({ id: selected.id, payload: { internal_notes: notes } },
      { onSuccess: () => toast.success("Notas internas guardadas") });
  };

  const saveManagedBy = (name) => {
    if (!selected) return;
    update.mutate({ id: selected.id, payload: { managed_by: name } },
      { onSuccess: () => toast.success("Responsable actualizado") });
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1100px] mx-auto overflow-y-auto h-full" data-testid="citas-page">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <p className="text-sm text-[#8A7E70]">Citas solicitadas por los pacientes. Haz clic en una cita para ver todos los datos y añadir notas internas.</p>
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#6B6157] bg-[#F1EBE1] rounded-full px-3 py-1.5">
          <CalendarClock className="h-3.5 w-3.5" /> {citas.length} citas
        </span>
      </div>

      <div className="space-y-3" data-testid="citas-list">
        {citas.length === 0 && (
          <div className="rounded-2xl border border-dashed border-[#E0D3BC] bg-white p-12 text-center text-sm text-[#A79E90]">
            Aún no hay citas solicitadas. Aparecerán aquí cuando un paciente pida cita por WhatsApp o Instagram.
          </div>
        )}
        {citas.map((cita) => {
          const Icon = CH_ICON[cita.channel] || MessageCircle;
          const st = STATUS[cita.appointment_status] || STATUS.pendiente;
          return (
            <button
              key={cita.id}
              data-testid="cita-card"
              onClick={() => setOpenId(cita.id)}
              className="w-full text-left rounded-2xl border border-[#E7E2DA] bg-white p-4 flex items-center gap-4 hover:border-[#D8CDBB] hover:shadow-sm transition-all"
            >
              <span className="h-12 w-12 rounded-full flex items-center justify-center text-white shrink-0" style={{ background: CH_COLOR[cita.channel] || "#1A1A1A" }}>
                <Icon className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-[#1A1A1A]">{cita.name}</div>
                <div className="text-xs text-[#8A7E70] flex items-center gap-2 mt-0.5 flex-wrap">
                  {cita.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {cita.phone}</span>}
                  <span className="text-[#B8ADA0]">·</span>
                  <span>{CH_NAME[cita.channel]}</span>
                  <span className="text-[#B8ADA0]">·</span>
                  <span>{fmtShort(cita.created_at)}</span>
                  {cita.internal_notes && <span className="inline-flex items-center gap-1 text-[#B45309]"><StickyNote className="h-3 w-3" /> con notas</span>}
                </div>
              </div>
              <span
                data-testid="cita-status-btn"
                role="button"
                onClick={(e) => cycleStatus(cita, e)}
                className={`h-9 px-4 rounded-full border text-sm font-semibold transition-colors shrink-0 ${st.cls}`}
              >
                {st.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Panel lateral de detalle */}
      <Sheet open={!!openId} onOpenChange={(o) => { if (!o) { saveNotes(); setOpenId(null); } }}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="cita-detail-panel">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <span className="h-8 w-8 rounded-full flex items-center justify-center text-white" style={{ background: CH_COLOR[selected.channel] }}>
                    {(CH_ICON[selected.channel] || MessageCircle) && (() => { const I = CH_ICON[selected.channel] || MessageCircle; return <I className="h-4 w-4" />; })()}
                  </span>
                  {selected.name}
                </SheetTitle>
                <SheetDescription>Ficha de la cita y notas internas del equipo.</SheetDescription>
              </SheetHeader>

              <div className="space-y-4 py-5">
                <div className="grid grid-cols-1 gap-3">
                  <InfoRow icon={Phone} label="Teléfono" value={selected.phone || "No facilitado"} testid="detail-phone" />
                  <InfoRow icon={CH_ICON[selected.channel] || MessageCircle} label="Canal" value={CH_NAME[selected.channel]} testid="detail-channel" />
                  <InfoRow icon={CalendarClock} label="Fecha" value={fmtDate(selected.created_at)} testid="detail-date" />
                </div>

                <div className="rounded-xl border border-[#EDE7DE] bg-[#FAF8F5] p-3" data-testid="detail-message">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-[#8A7E70] mb-1">
                    <MessageSquareText className="h-3.5 w-3.5" /> Mensaje que dejó
                  </div>
                  <p className="text-sm text-[#2A2723]">{selected.message || "—"}</p>
                </div>

                <div>
                  <Label className="mb-1.5 block">Gestionado por</Label>
                  <Input
                    data-testid="detail-managed-by"
                    defaultValue={selected.managed_by || ""}
                    onBlur={(e) => { if (e.target.value !== (selected.managed_by || "")) saveManagedBy(e.target.value); }}
                    placeholder="Nombre del responsable"
                  />
                </div>

                <div>
                  <Label className="mb-1.5 block">Estado</Label>
                  <Button
                    data-testid="detail-status-btn"
                    onClick={() => cycleStatus(selected)}
                    variant="outline"
                    className={`h-10 ${STATUS[selected.appointment_status]?.cls || STATUS.pendiente.cls}`}
                  >
                    {STATUS[selected.appointment_status]?.label || "Pendiente"}
                  </Button>
                  <p className="text-[11px] text-[#A79E90] mt-1">Haz clic para cambiar: Pendiente → Confirmada → Cancelada</p>
                </div>

                <div>
                  <Label className="mb-1.5 flex items-center gap-1.5"><StickyNote className="h-4 w-4" /> Notas internas</Label>
                  <Textarea
                    data-testid="detail-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    onBlur={saveNotes}
                    placeholder="Ej.: Se llamó pero no contestó. Confirmó por teléfono…"
                    rows={5}
                    maxLength={4000}
                    className="resize-none"
                  />
                  <div className="flex justify-end mt-2">
                    <Button data-testid="detail-save-notes" onClick={saveNotes} disabled={update.isPending} className="h-9 bg-[#1A1A1A] hover:bg-black text-white">
                      {update.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar notas"}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

const InfoRow = ({ icon: Icon, label, value, testid }) => (
  <div className="flex items-center gap-3" data-testid={testid}>
    <span className="h-9 w-9 rounded-lg bg-[#F1EBE1] flex items-center justify-center shrink-0">
      <Icon className="h-4 w-4 text-[#6B6157]" />
    </span>
    <div className="min-w-0">
      <div className="text-[11px] text-[#A79E90]">{label}</div>
      <div className="text-sm text-[#2A2723] capitalize truncate">{value}</div>
    </div>
  </div>
);
